import {useEffect, useState} from 'react';
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Box,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {Warning} from '@mui/icons-material';

import {useAuth} from 'src/components/AuthProvider.tsx';
import newLogo from 'src/assets/logo_no_background.png';
import {ReactComponent as EmailIcon} from 'src/assets/icons/email.svg';
import {
  authServiceLogin,
  authServiceRegisterCallback,
  authServiceResetPassword,
  authServiceSendForgetPasswordEmail,
  tenantServiceSendInviteCallback,
  userPublicServiceRegisterOrganization,
} from 'src/apis/autoGenerate/services';
import message from 'src/components/Message';
import {useAppDispatch} from 'src/redux/hooks';
import {
  setUserId,
  setJwtToken,
  setRole,
  setOrganization,
  getUserDetails,
} from 'src/redux/slice/accountSlice';
import {AuthLoginResponse} from 'src/apis/autoGenerate/types/auth';
import {Role} from 'src/apis/autoGenerate/types/role';
import {UserRegisterOrganizationReq} from 'src/apis/autoGenerate/types/user';

import LoadingButton from '@mui/lab/LoadingButton';
import LocalDB from 'src/utils/localDB';
import {getHomePath} from 'src/utils/contextPreferences';
import {useUrlSearchParams} from 'src/hooks';
import apiInstance from 'src/apis/config/axiosConfig';
import {SvgIcon, SvgIconProps} from '@mui/material';

export enum LoginState {
  Login = 'Login',
  SignUp = 'SignUp',
  EmailVerified = 'EmailVerified',
  ResetPassword = 'ResetPassword',
  ResetNewPassword = 'ResetNewPassword',
  ResetPasswordCheckEmail = 'ResetPasswordCheckEmail',
  EmailActivation = 'EmailActivation',
  InviteTenant = 'InviteTenant',
}

interface ShowPasswordState {
  password: boolean;
  newPassword: boolean;
  confirmPassword: boolean;
}

interface PasswordStrength {
  score: number; // 0-100
  messages: string[];
}

function getTitle(state: LoginState) {
  let title = '';
  switch (state) {
    case LoginState.Login:
      title = 'Login to continue';
      break;
    case LoginState.SignUp:
      title = 'Create New Organization';
      break;
    case LoginState.EmailVerified:
      title = 'Email address verified';
      break;
    case LoginState.ResetPassword:
      title = 'Forget Password?';
      break;
    case LoginState.ResetPasswordCheckEmail:
      title = 'Forget Password?';
      break;
    case LoginState.ResetNewPassword:
      title = 'Reset Password';
      break;
  }
  return title;
}

export function TechLogo(props: SvgIconProps) {
  return (
    <SvgIcon
      {...props}
      viewBox="0 0 100 100"
      sx={{
        fontSize: '72px',
        ...props.sx,
      }}
    >
      {/* Soundwave shape representing "Vox" */}
      <path
        d="M10,50 Q25,30 30,50 Q35,70 40,50 Q45,30 50,50 Q55,70 60,50 Q65,30 70,50 Q75,70 80,50 Q85,30 90,50"
        stroke="#1A4065"
        strokeWidth="4"
        fill="none"
      />

      {/* Smart connection */}
      <circle cx="20" cy="50" r="3" fill="#1A4065" />
      <circle cx="50" cy="50" r="3" fill="#1A4065" />
      <circle cx="80" cy="50" r="3" fill="#1A4065" />

      <rect x="10" y="60" width="80" height="4" rx="2" fill="#1A4065" />
      <rect x="25" y="70" width="50" height="4" rx="2" fill="#1A4065" />
    </SvgIcon>
  );
}

export default function SignInSide() {
  const [verifyCode, email, loginStateFromUrl, id, sessionExpired] = useUrlSearchParams([
    'verifyCode',
    'email',
    'state',
    'id',
    'sessionExpired',
  ]) as string[];

  const {setToken} = useAuth();
  const navigate = useNavigate();
  const [loginState, setLoginState] = useState(
    (loginStateFromUrl as LoginState) || LoginState.Login
  );
  const [showPassword, setShowPassword] = useState<ShowPasswordState>({
    password: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    messages: [],
  });
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [sendEmail, setSendEmail] = useState('');

  // Show session expired message if redirected from SESSION_INVALID error
  useEffect(() => {
    if (sessionExpired === 'true') {
      message.warning(
        'Your account has been logged in from another device. Please log in again.',
        5000
      );
    }
  }, [sessionExpired]);

  useEffect(() => {
    if (loginState === LoginState.InviteTenant) {
      setLoading(true);
      tenantServiceSendInviteCallback(id, {email, verifyCode, id})
        .then(() => {
          message.success('Add success!');
          setInviteSuccess(true);
        })
        .catch(err => {
          const errorMessage = err?.message || err?.response?.data?.error || 'An error occurred';
          message.error(errorMessage);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, []);

  const checkPasswordStrength = (password: string): PasswordStrength => {
    const strength: PasswordStrength = {
      score: 0,
      messages: [],
    };

    // Password length check
    if (password.length < 8) {
      strength.messages.push('Password must be at least 8 characters');
    } else {
      strength.score += 25;
    }

    // Contains number check
    if (!/\d/.test(password)) {
      strength.messages.push('Include at least one number');
    } else {
      strength.score += 25;
    }

    // Contains lowercase letter check
    if (!/[a-z]/.test(password)) {
      strength.messages.push('Include at least one lowercase letter');
    } else {
      strength.score += 25;
    }

    // Contains uppercase letter check
    if (!/[A-Z]/.test(password)) {
      strength.messages.push('Include at least one uppercase letter');
    } else {
      strength.score += 25;
    }

    return strength;
  };

  const getStrengthColor = (score: number) => {
    if (score <= 25) return '#f44336'; // Red
    if (score <= 50) return '#ff9800'; // Orange
    if (score <= 75) return '#ffc107'; // Yellow
    return '#4caf50'; // Green
  };

  const handleClickShowPassword = (key: keyof ShowPasswordState) =>
    setShowPassword({...showPassword, [key]: !showPassword[key]});

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const password = event.target.value;
    setPasswordStrength(checkPasswordStrength(password));
  };

  const handleSignUpClick = () => {
    setShowWarningDialog(true);
  };

  const handleWarningDialogClose = () => {
    setShowWarningDialog(false);
  };

  const handleProceedToSignUp = () => {
    setShowWarningDialog(false);
    setLoginState(LoginState.SignUp);
  };

  const handleLogin = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const loginInfo = {
      email: data.get('email') as string,
      password: data.get('password') as string,
    };
    setLoading(true);
    authServiceLogin(loginInfo)
      .then(res => {
        const r = res.data;

        // Check if user is a tenant
        if (r.role?.name === 'tenant') {
          message.error('Tenants cannot access the web portal. Please use the mobile app.');
          setLoading(false);
          return;
        }

        // Store role and user ID
        LocalDB.set(LocalDB.alias.role, JSON.stringify(r.role));
        LocalDB.set(LocalDB.alias.userId, r.userId);

        // Store organization if available
        if (r.organization) {
          LocalDB.set(LocalDB.alias.organization, JSON.stringify(r.organization));
        }

        // Store both tokens
        LocalDB.set(LocalDB.alias.token, r.jwtToken);
        LocalDB.set(LocalDB.alias.refreshToken, r.refreshToken);

        // Store session information if available
        if (r.sessionId) {
          LocalDB.set(LocalDB.alias.sessionId, r.sessionId);
          localStorage.setItem('sessionId', r.sessionId);
        }
        if (r.sessionExpiresAt) {
          LocalDB.set(LocalDB.alias.sessionExpiresAt, r.sessionExpiresAt);
          localStorage.setItem('sessionExpiresAt', r.sessionExpiresAt);
        }

        // Also store in localStorage for redundancy
        localStorage.setItem('token', r.jwtToken);
        localStorage.setItem('refreshToken', r.refreshToken);
        localStorage.setItem('userId', r.userId);

        // Update Redux state
        setToken(r.jwtToken);
        dispatch(setUserId(r.userId));
        dispatch(setJwtToken(r.jwtToken));
        dispatch(setRole(r.role as Role));

        // Store organization in Redux
        if (r.organization) {
          dispatch(setOrganization(r.organization));
        }

        getUserDetails(r.userId);
        navigate(getHomePath(r.userId, (r.role as Role)?.name), {replace: true});
      })
      .catch(err => {
        // Interceptors reject with { message } from response data.error (no err.response)
        const errorMessage =
          err?.response?.data?.error || err?.message || 'Login failed. Please try again.';
        message.error(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSignUp = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    const signupData: UserRegisterOrganizationReq = {
      email: data.get('email') as string,
      firstName: data.get('firstName') as string,
      lastName: data.get('lastName') as string,
      password: data.get('password') as string,
      organizationName: data.get('organizationName') as string,
      organizationDescription: data.get('organizationDescription') as string,
    };

    const confirmPassword = data.get('confirmPassword') as string;

    // Validation
    if (signupData.password !== confirmPassword) {
      message.error('Passwords do not match');
      return;
    }

    if (passwordStrength.score < 100) {
      message.error('Please ensure your password meets all requirements');
      return;
    }

    setLoading(true);
    userPublicServiceRegisterOrganization(signupData)
      .then(response => {
        message.success('Registration successful! Please check your email to verify your account.');
        // Optionally redirect to a success page or back to login
        setLoginState(LoginState.Login);
      })
      .catch(err => {
        const errorMessage =
          err.response?.data?.message || err.message || 'Registration failed. Please try again.';
        message.error(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResetNewPassword = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const [newPassword, confirmPassword] = [data.get('newPassword'), data.get('confirmPassword')];
    if (newPassword !== confirmPassword) {
      message.error('Your confirmed password and new password do not match.');
      return;
    }

    setLoading(true);
    authServiceResetPassword({email, verifyCode, password: newPassword as string})
      .then(res => {
        message.success('reset password success!');
      })
      .catch(err => {
        message.error(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSignUpPassword = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const [password, confirmPassword] = [data.get('password'), data.get('confirmPassword')];
    if (password !== confirmPassword) {
      message.error('Your confirmed password and new password do not match.');
      return;
    }
    setLoading(true);
    authServiceRegisterCallback({
      email,
      password: confirmPassword as string,
      verifyCode,
    })
      .then(() => {
        message.success('Sign up success!');
      })
      .catch(err => {
        message.error(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSendLinkClick = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = data.get('email') as string;
    handleSendLink(email);
  };

  const handleSendLink = (email: string) => {
    setSendEmail(email);
    setLoading(true);
    authServiceSendForgetPasswordEmail({email})
      .then(res => {
        setLoginState(LoginState.ResetPasswordCheckEmail);
        message.success('send email success');
      })
      .catch(err => {
        message.error(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  function renderContent() {
    switch (loginState) {
      case LoginState.Login:
        return (
          <Box component="form" onSubmit={handleLogin} sx={{mt: 1}}>
            <FormControl fullWidth required size="small" sx={{mt: 1}}>
              <InputLabel htmlFor="email">Enter your email</InputLabel>
              <OutlinedInput
                name="email"
                id="email"
                label="Enter your email"
                autoComplete="email"
                autoFocus
              />
            </FormControl>
            <FormControl fullWidth required size="small" margin="normal">
              <InputLabel htmlFor="password">Enter Password</InputLabel>
              <OutlinedInput
                name="password"
                id="password"
                label="Enter password"
                autoComplete="current-password"
                type={showPassword.password ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword('password')}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword.password ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
            <LoadingButton
              type="submit"
              fullWidth
              variant="contained"
              sx={{mt: 1, mb: 2}}
              loading={loading}
              loadingPosition="start"
              startIcon={<></>}
            >
              Log In
            </LoadingButton>
            <Typography variant="caption" align="center" display="block" sx={{mb: 2}}>
              By signing in, you agree with our{' '}
              <Link href="https://www.entrioai.com/terms-and-services" underline="hover">
                Terms
              </Link>{' '}
              and{' '}
              <Link href="https://www.entrioai.com/privacy-policy" underline="hover">
                Policies
              </Link>
            </Typography>
            <Grid container justifyContent="center" spacing={2}>
              <Grid item>
                <Button onClick={() => setLoginState(LoginState.ResetPassword)}>
                  <Typography variant="body2" fontWeight="500">
                    Forgot password?
                  </Typography>
                </Button>
              </Grid>
              <Grid item>
                <Button onClick={handleSignUpClick}>
                  <Typography variant="body2" fontWeight="500">
                    Create Organization
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Box>
        );

      case LoginState.SignUp:
        return (
          <Box component="form" onSubmit={handleSignUp} sx={{mt: 1}}>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
              Create a new organization account
            </Typography>

            {/* Personal Information */}
            <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 600}}>
              Personal Information
            </Typography>
            <Grid container spacing={2} sx={{mb: 2}}>
              <Grid item xs={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel htmlFor="firstName">First Name</InputLabel>
                  <OutlinedInput
                    name="firstName"
                    id="firstName"
                    label="First Name"
                    autoComplete="given-name"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel htmlFor="lastName">Last Name</InputLabel>
                  <OutlinedInput
                    name="lastName"
                    id="lastName"
                    label="Last Name"
                    autoComplete="family-name"
                  />
                </FormControl>
              </Grid>
            </Grid>

            <FormControl fullWidth required size="small" sx={{mb: 2}}>
              <InputLabel htmlFor="email">Email Address</InputLabel>
              <OutlinedInput
                name="email"
                id="email"
                label="Email Address"
                autoComplete="email"
                type="email"
              />
            </FormControl>

            {/* Organization Information */}
            <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 600}}>
              Organization Information
            </Typography>
            <FormControl fullWidth required size="small" sx={{mb: 2}}>
              <InputLabel htmlFor="organizationName">Organization Name</InputLabel>
              <OutlinedInput
                name="organizationName"
                id="organizationName"
                label="Organization Name"
                autoComplete="organization"
              />
            </FormControl>

            <FormControl fullWidth size="small" sx={{mb: 2}}>
              <InputLabel htmlFor="organizationDescription">Organization Description</InputLabel>
              <OutlinedInput
                name="organizationDescription"
                id="organizationDescription"
                label="Organization Description"
                multiline
                rows={2}
              />
            </FormControl>

            {/* Password Section */}
            <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 600}}>
              Password
            </Typography>
            <FormControl fullWidth required size="small" sx={{mb: 1}}>
              <InputLabel htmlFor="password">Password</InputLabel>
              <OutlinedInput
                name="password"
                id="password"
                label="Password"
                type={showPassword.password ? 'text' : 'password'}
                onChange={handlePasswordChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword('password')}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword.password ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>

            {/* Password strength indicator */}
            <Box sx={{mb: 2}}>
              <LinearProgress
                variant="determinate"
                value={passwordStrength.score}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: getStrengthColor(passwordStrength.score),
                  },
                }}
              />
              <Box sx={{mt: 1}}>
                {passwordStrength.messages.map((msg, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{fontSize: '0.7rem'}}
                  >
                    • {msg}
                  </Typography>
                ))}
                {passwordStrength.score === 100 && (
                  <Typography variant="caption" color="success.main" display="block">
                    Password strength: Strong
                  </Typography>
                )}
              </Box>
            </Box>

            <FormControl fullWidth required size="small" sx={{mb: 2}}>
              <InputLabel htmlFor="confirmPassword">Confirm Password</InputLabel>
              <OutlinedInput
                name="confirmPassword"
                id="confirmPassword"
                label="Confirm Password"
                type={showPassword.confirmPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword('confirmPassword')}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>

            <Typography variant="caption" sx={{mb: 2, display: 'block'}}>
              By creating an account, you agree with our{' '}
              <Link href="https://www.entrioai.com/terms-and-services" underline="hover">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link href="https://www.entrioai.com/privacy-policy" underline="hover">
                Privacy Policy
              </Link>
            </Typography>

            <LoadingButton
              type="submit"
              fullWidth
              variant="contained"
              sx={{mt: 1, mb: 2}}
              loading={loading}
              disabled={passwordStrength.score < 100}
              loadingPosition="start"
              startIcon={<></>}
            >
              Create Organization
            </LoadingButton>

            <Grid container justifyContent="center">
              <Button onClick={() => setLoginState(LoginState.Login)}>
                <Typography variant="body2" fontWeight="500">
                  Back to Login
                </Typography>
              </Button>
            </Grid>
          </Box>
        );

      case LoginState.EmailVerified:
        return (
          <Box component="form" onSubmit={handleSignUpPassword} sx={{width: '100%'}}>
            <Typography variant="body2" textAlign="center" sx={{color: '#71839B'}}>
              Finish setting your account
            </Typography>
            <Grid container alignContent="flex-start" direction="column" mt={1} spacing={2}>
              <Grid item>
                <Typography variant="body2" sx={{color: '#71839B'}}>
                  Email Address
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {email}
                </Typography>
              </Grid>
              <Grid item>
                <FormControl fullWidth required size="small">
                  <InputLabel htmlFor="password">Enter Password</InputLabel>
                  <OutlinedInput
                    name="password"
                    id="password"
                    label="Enter password"
                    autoComplete="current-password"
                    type={showPassword.password ? 'text' : 'password'}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => handleClickShowPassword('password')}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword.password ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl fullWidth required size="small">
                  <InputLabel htmlFor="confirmPassword">Confirm Password</InputLabel>
                  <OutlinedInput
                    name="confirmPassword"
                    id="confirmPassword"
                    label="Confirm password"
                    autoComplete="current-password"
                    type={showPassword.confirmPassword ? 'text' : 'password'}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => handleClickShowPassword('confirmPassword')}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item>
                <Typography variant="caption">
                  By signing up, I accept the{' '}
                  <Link href="https://www.entrioai.com/terms-and-services">
                    Entrio AI,Inc Terms of Service{' '}
                  </Link>
                  and acknowledge the{' '}
                  <Link href="https://www.entrioai.com/privacy-policy">Privacy Policy</Link>
                </Typography>
              </Grid>
              <Grid item>
                <LoadingButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  loading={loading}
                  loadingPosition="start"
                  startIcon={<></>}
                >
                  Sign Up
                </LoadingButton>
              </Grid>
            </Grid>
          </Box>
        );

      case LoginState.ResetPassword:
        return (
          <Box component="form" onSubmit={handleSendLinkClick} sx={{mt: 1}}>
            <Typography variant="body2" textAlign="center" sx={{color: '#71839B', mb: 2}}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>
            <FormControl fullWidth required size="small" sx={{mt: 1}}>
              <InputLabel htmlFor="email">Enter your email</InputLabel>
              <OutlinedInput
                name="email"
                id="email"
                label="Enter your email"
                autoComplete="email"
                autoFocus
              />
            </FormControl>
            <LoadingButton
              type="submit"
              fullWidth
              variant="contained"
              sx={{mt: 2, mb: 2}}
              loading={loading}
              loadingPosition="start"
              startIcon={<></>}
            >
              Send Reset Link
            </LoadingButton>
            <Grid container justifyContent="center">
              <Button onClick={() => setLoginState(LoginState.Login)}>
                <Typography variant="body2" fontWeight="500">
                  Back to Login
                </Typography>
              </Button>
            </Grid>
          </Box>
        );

      case LoginState.ResetPasswordCheckEmail:
        return (
          <Box sx={{mt: 1}}>
            <Grid container direction="column" justifyContent="center" alignItems="center">
              <Grid item>
                <Stack spacing={1} alignItems="center">
                  <Box
                    component="img"
                    src={newLogo}
                    alt="Entrio AI,Inc Logo"
                    sx={{
                      width: {xs: '140px', sm: '160px'},
                      height: 'auto',
                    }}
                  />
                  <Typography variant="h5" component="h1" fontWeight="bold">
                    Entrio AI,Inc
                  </Typography>
                </Stack>
              </Grid>
              <Typography variant="body2" textAlign="center" sx={{color: '#71839B'}}>
                We sent a recovery link to you at
              </Typography>
              <Typography variant="subtitle1" textAlign="center" fontWeight={600} marginBottom={2}>
                {sendEmail}
              </Typography>
            </Grid>
            <LoadingButton
              variant="text"
              sx={{mt: 3, mb: 2}}
              loading={loading}
              fullWidth
              loadingPosition="start"
              startIcon={<></>}
              onClick={() => handleSendLink(sendEmail)}
            >
              <Typography variant="body2" fontWeight="500">
                Resend Verification Email
              </Typography>
            </LoadingButton>
          </Box>
        );
      case LoginState.ResetNewPassword:
        return (
          <Box component="form" onSubmit={handleResetNewPassword} sx={{mt: 1}}>
            {/* <FormControl fullWidth required size="small">
              <InputLabel htmlFor="oldPassword">Old Password</InputLabel>
              <OutlinedInput
                name="oldPassword"
                id="oldPassword"
                label="Old password"
                autoComplete="current-password"
                type={showPassword.password ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword('password')}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword.password ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl> */}
            <FormControl fullWidth required size="small" sx={{my: 2}}>
              <InputLabel htmlFor="newPassword">New Password</InputLabel>
              <OutlinedInput
                name="newPassword"
                id="newPassword"
                label="New password"
                autoComplete="current-password"
                type={showPassword.newPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword('newPassword')}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword.newPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
            <FormControl fullWidth required size="small">
              <InputLabel htmlFor="confirmPassword">Confirm Password</InputLabel>
              <OutlinedInput
                name="confirmPassword"
                id="confirmPassword"
                label="Confirm password"
                autoComplete="current-password"
                type={showPassword.confirmPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword('confirmPassword')}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
            <LoadingButton
              type="submit"
              fullWidth
              variant="contained"
              sx={{mt: 3, mb: 2}}
              loading={loading}
              loadingPosition="start"
              startIcon={<></>}
            >
              Reset Password
            </LoadingButton>
          </Box>
        );
    }
  }

  if (loginState === LoginState.InviteTenant) {
    return (
      <h1 style={{textAlign: 'center'}}>
        {loading ? 'loading...' : inviteSuccess ? 'Success' : 'Something wrong'}
      </h1>
    );
  }

  if (loginState === LoginState.EmailActivation) {
    return <></>;
  }

  return (
    <Grid container component="main" sx={{height: '100vh'}}>
      <CssBaseline />

      {/* Warning Dialog */}
      <Dialog open={showWarningDialog} onClose={handleWarningDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Warning color="warning" />
          Important Notice
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{mb: 2}}>
            <Typography variant="body1" fontWeight="600" gutterBottom>
              Registration is for creating a new organization by the Super Administrator only.
            </Typography>
            <Typography variant="body2">
              If you're joining an existing organization, please wait for an invitation from your
              admin.
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            By proceeding, you will create a new organization and become its Super Administrator.
            This should only be done if you are setting up a new property management organization.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarningDialogClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleProceedToSignUp} variant="contained" color="warning">
            I Understand, Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Left section - Login Form */}
      <Grid
        item
        xs={12}
        sm={6}
        md={5}
        component={Paper}
        elevation={6}
        square
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: {xs: '100%', sm: 450},
            px: {xs: 2, sm: 3},
            py: {xs: 3, sm: 4},
          }}
        >
          {/* Logo section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: {xs: 3, sm: 4},
            }}
          >
            <Box
              component="img"
              src={newLogo}
              alt="Entrio AI Logo"
              sx={{
                width: {xs: '140px', sm: '160px'},
                height: 'auto',
                mb: 1,
              }}
            />
          </Box>

          <Typography
            variant="h6"
            fontWeight={600}
            mb={3}
            sx={{
              fontSize: {xs: '1rem', sm: '1.25rem'},
            }}
          >
            {getTitle(loginState)}
          </Typography>

          {renderContent()}
        </Box>
      </Grid>

      {/* Right section - Welcome Message */}
      <Grid
        item
        xs={false}
        sm={6}
        md={7}
        sx={{
          position: 'relative',
          bgcolor: '#f5f8fa',
          display: {xs: 'none', sm: 'block'},
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: '#e8f0fe',
            backgroundImage: 'url(/abstract-pattern.png)',
            backgroundSize: 'cover',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 4,
          }}
        >
          <Typography variant="h3" fontWeight="bold" color="#1A4065" mb={3}>
            Welcome to entrio
          </Typography>
          <Typography variant="h6" color="#555" textAlign="center" maxWidth={600}>
            Smart property management solutions for modern communities
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}
